import React from "react";
import { DataTable, Column } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { Edit, Image } from "lucide-react";
import { Link } from "react-router-dom";
import { Amenity } from "@/types/amenity";
import AmenityDeleteButton from "@/components/pages/amenities/components/AmenityDeleteButton";
import { Switch } from "@/components/ui/switch";
import { useUpdateAmenity } from "@/components/pages/amenities/hooks/amenities.hooks";
import { useTranslation } from "react-i18next";

type SortOrder = "asc" | "desc" | null;

type Props = {
  data: Amenity[];
  isLoading: boolean;
  sortBy: string | null;
  sortOrder: SortOrder;
  onSort: (field: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

const AmenitiesTable: React.FC<Props> = ({
  data,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const { mutate: updateAmenity, isPending: isUpdating } = useUpdateAmenity();
  const { t } = useTranslation();

  const columns: Column<Amenity>[] = [
    {
      key: "isActive",
      header: t("amenities.table.status"),
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center justify-center gap-2">
          <Switch
            checked={row.isActive}
            onCheckedChange={(val) =>
              updateAmenity({ id: row.id, data: { isActive: val } })
            }
            disabled={isUpdating}
          />
        </div>
      ),
    },
    {
      key: "photoId",
      header: t("amenities.table.photo"),
      className: "text-center",
      render: (_, row) => (
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded border border-gray-300 bg-gray-100">
          <PresignedImageWithPlaceholder
            className={"h-10 w-10 overflow-hidden object-cover"}
            photo={row.photoId || undefined}
            placeholder={<Image className="h-6 w-6 text-gray-400" />}
          />
        </div>
      ),
    },
    {
      key: "name",
      header: t("amenities.table.name"),
      sortable: true,
      className: "text-center",
    },
    {
      key: "category",
      header: t("amenities.table.category"),
      sortable: true,
      className: "text-center",
    },
    {
      key: "description",
      header: t("amenities.table.description"),
      sortable: true,
      className: "text-center",
      render: (value) => (
        <span className="line-clamp-3 inline-block max-w-[400px] text-left">
          {value}
        </span>
      ),
    },
    {
      key: "services",
      header: t("amenities.table.services"),
      sortable: true,
      className: "text-center",
      render: (_, row) => <span>{row.services.length}</span>,
    },
    {
      key: "actions",
      header: t("amenities.table.actions"),
      className: "text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center gap-1">
          <Button
            asChild
            variant="new_ghost"
            size="icon_sm"
            title={t("amenities.table.edit")}
          >
            <Link to={`/amenities/${row.id}`}>
              <Edit />
            </Link>
          </Button>
          <AmenityDeleteButton amenityId={row.id} />
        </div>
      ),
    },
  ];

  return (
    <DataTable<Amenity>
      data={data}
      columns={columns}
      loading={isLoading}
      sorting={{
        field: sortBy ?? null,
        direction: sortOrder ?? null,
        onSort,
      }}
      pagination={{
        currentPage,
        totalPages: totalPages || 1,
        onPageChange,
      }}
      withPagination={true}
      withSorting={true}
      emptyMessage={t("amenities.table.empty")}
    />
  );
};

export default AmenitiesTable;
